import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    name: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const { login, register, user } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in
  if (user) {
    return <Navigate to="/dashboard" />;
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username) {
      newErrors.username = 'Username is required';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    if (!isLogin) {
      if (!formData.name) {
        newErrors.name = 'Name is required';
      }
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      let result;
      if (isLogin) {
        result = await login(formData.username, formData.password);
      } else {
        result = await register(formData);
      }

      if (result.success) {
        navigate('/dashboard');
      } else {
        setErrors({ submit: result.error || 'Authentication failed' });
      }
    } catch (error) {
      setErrors({ submit: 'An unexpected error occurred' });
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setFormData({
      username: '',
      password: '',
      name: '',
      confirmPassword: ''
    });
    setErrors({});
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      <div className="max-w-md w-full glass-panel p-8 sm:p-10 z-10">
        <div>
          <h2 className="mt-2 text-center text-3xl font-light text-slate-800 tracking-tight">
            {isLogin ? 'Sign in to your account' : 'Create your account'}
          </h2>
          <p className="mt-4 text-center text-[15px] text-slate-500">
            {isLogin ? "Don't have an account? " : "Already have an account? "}
            <button
              onClick={toggleMode}
              className="font-medium text-primary-600 hover:text-primary-700 hover:underline transition-all duration-200"
            >
              {isLogin ? 'Sign up' : 'Sign in'}
            </button>
          </p>
        </div>

        <form className="mt-10 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-5">
            {!isLogin && (
              <div>
                <label htmlFor="name" className="block text-[13px] font-medium text-slate-700 tracking-wide uppercase mb-1.5">
                  Full Name
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`appearance-none block w-full px-4 py-2.5 bg-white/50 backdrop-blur-sm border ${errors.name ? 'border-red-400 focus:ring-red-100' : 'border-slate-200/60'
                    } placeholder-slate-400 text-slate-800 rounded-xl focus:outline-none focus:bg-white focus:ring-4 focus:ring-slate-100 focus:border-slate-400 sm:text-sm transition-all duration-300`}
                  placeholder="Enter your full name"
                />
                {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
              </div>
            )}

            <div>
              <label htmlFor="username" className="block text-[13px] font-medium text-slate-700 tracking-wide uppercase mb-1.5">
                Username
              </label>
              <input
                id="username"
                name="username"
                type="text"
                autoComplete="username"
                value={formData.username}
                onChange={handleInputChange}
                className={`appearance-none block w-full px-4 py-2.5 bg-white/50 backdrop-blur-sm border ${errors.username ? 'border-red-400 focus:ring-red-100' : 'border-slate-200/60'
                  } placeholder-slate-400 text-slate-800 rounded-xl focus:outline-none focus:bg-white focus:ring-4 focus:ring-slate-100 focus:border-slate-400 sm:text-sm transition-all duration-300`}
                placeholder="Enter your username"
              />
              {errors.username && <p className="mt-1 text-sm text-red-600">{errors.username}</p>}
            </div>

            <div>
              <label htmlFor="password" className="block text-[13px] font-medium text-slate-700 tracking-wide uppercase mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete={isLogin ? 'current-password' : 'new-password'}
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`appearance-none block w-full px-4 py-2.5 pr-10 bg-white/50 backdrop-blur-sm border ${errors.password ? 'border-red-400 focus:ring-red-100' : 'border-slate-200/60'
                    } placeholder-slate-400 text-slate-800 rounded-xl focus:outline-none focus:bg-white focus:ring-4 focus:ring-slate-100 focus:border-slate-400 sm:text-sm transition-all duration-300`}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-slate-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-slate-400" />
                  )}
                </button>
              </div>
              {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
            </div>

            {!isLogin && (
              <div>
                <label htmlFor="confirmPassword" className="block text-[13px] font-medium text-slate-700 tracking-wide uppercase mb-1.5">
                  Confirm Password
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className={`appearance-none block w-full px-4 py-2.5 bg-white/50 backdrop-blur-sm border ${errors.confirmPassword ? 'border-red-400 focus:ring-red-100' : 'border-slate-200/60'
                    } placeholder-slate-400 text-slate-800 rounded-xl focus:outline-none focus:bg-white focus:ring-4 focus:ring-slate-100 focus:border-slate-400 sm:text-sm transition-all duration-300`}
                  placeholder="Confirm your password"
                />
                {errors.confirmPassword && <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>}
              </div>
            )}
          </div>

          {errors.submit && (
            <div className="rounded-xl bg-red-50/80 backdrop-blur-sm border border-red-200 p-4">
              <p className="text-sm text-red-600 font-medium">{errors.submit}</p>
            </div>
          )}

          <div className="pt-2">
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-xl text-white bg-slate-800 hover:bg-slate-700 shadow-sm shadow-slate-800/20 hover:scale-[1.02] focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {isLogin ? 'Signing in...' : 'Creating account...'}
                </div>
              ) : (
                isLogin ? 'Sign in' : 'Create account'
              )}
            </button>
          </div>

          {/* {isLogin && (
            <div className="text-center">
              <p className="text-sm text-gray-600">
                Demo accounts:
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Admin: adminuser | User: regularuser
              </p>
              <p className="text-xs text-gray-500">
                Password: password123
              </p>
            </div>
          )} */}
        </form>
      </div>
    </div>
  );
};

export default Login;
